'use client'

import { useState } from 'react'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ContactFormData {
  name: string
  email: string
  company: string
  phone: string
  request: string
}

export default function ContactForm() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    request: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      // Import siloApi dynamically to use the public submission endpoint
      const { siloApi } = await import('@/lib/api/silo-api')
      
      await siloApi.submitPublicContactForm({
        name: formData.name,
        email: formData.email,
        company: formData.company, // Send company at root level for business creation
        phone: formData.phone,
        request: formData.request,
        metadata: {
          source: 'contact_form',
          company: formData.company, // Keep in metadata for backward compatibility
          created_at: new Date().toISOString()
        }
      })

      setSubmitted(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  if (submitted) {
    return (
      <div className="max-w-md mx-auto text-center p-8 premium-card animate-scale-in">
        <div className="w-16 h-16 bg-eskerium-blue/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="w-8 h-8 text-eskerium-blue" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
        <p className="text-white/70">
          Your message has been received. We&apos;ll get back to you within 24 hours.
        </p>
      </div>
    )
  }

  return (
    <div className="premium-card p-8 animate-fade-in">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
            Your Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="brand-input w-full text-white placeholder-white/50"
            placeholder="Enter your full name"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="brand-input w-full text-white placeholder-white/50"
            placeholder="your.email@example.com"
          />
        </div>

        {/* Company */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-white/80 mb-2">
            Company/Business Name *
          </label>
          <input
            type="text"
            id="company"
            name="company"
            required
            value={formData.company}
            onChange={handleChange}
            className="brand-input w-full text-white placeholder-white/50"
            placeholder="Enter your company name"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="brand-input w-full text-white placeholder-white/50"
            placeholder="Enter your phone number"
          />
        </div>

        {/* Request */}
        <div>
          <label htmlFor="request" className="block text-sm font-medium text-white/80 mb-2">
            How can we help you? *
          </label>
          <textarea
            id="request"
            name="request"
            required
            rows={6}
            value={formData.request}
            onChange={handleChange}
            className="brand-input w-full text-white placeholder-white/50 resize-vertical"
            placeholder="Tell us about your needs, questions, or interest in our products..."
          />
        </div>

        {/* Submit Button */}
        <div>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="premium"
            size="lg"
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send Message</span>
              </>
            )}
          </Button>
        </div>

        <p className="text-sm text-white/60 text-center">
          We typically respond within 24 hours during business days.
        </p>
      </form>
    </div>
  )
}